import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked'
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked'
import SwapHorizIcon from '@mui/icons-material/SwapHoriz'
import {
	Box,
	Button,
	Chip,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	IconButton,
} from '@mui/material'
import ListItem from '@mui/material/ListItem'
import Typography from '@mui/material/Typography'
import * as React from 'react'
import { useNavigate } from 'react-router-dom'
import { Scenario } from '../../types'
import ScenarioGanttMini from '../ScenarioGanttMini'

type Props = {
	scenario: Scenario
	disabled: boolean
	keyi: number
	handleSwitch: (scenario: Scenario) => void
}

// 自動制御(シナリオ)一覧の1行。ラジオ選択で「1つだけ稼働＝切り替え」を直感的に示す。
// 稼働中の制御が変わる操作なので、切り替えは確認ダイアログを挟む。
const ScenarioRow = (props: Props) => {
	const history = useNavigate()
	const { scenario, keyi, disabled } = props
	const active = scenario.isActive

	const [confirmOpen, setConfirmOpen] = React.useState(false)

	const requestSwitch = () => {
		if (!active && !disabled) setConfirmOpen(true)
	}
	const doSwitch = () => {
		props.handleSwitch(scenario)
		setConfirmOpen(false)
	}

	return (
		<ListItem
			key={scenario.id}
			data-test={`scenario-item-${keyi}`}
			sx={{ display: 'block', px: 0, py: 0.5 }}
		>
			<Box
				sx={{
					display: 'flex',
					alignItems: 'center',
					gap: 1.25,
					p: 1.25,
					borderRadius: 2,
					border: '1px solid',
					borderColor: active ? 'primary.main' : 'divider',
					borderLeft: '4px solid',
					borderLeftColor: active ? 'primary.main' : 'transparent',
					bgcolor: active ? 'action.selected' : 'background.paper',
					transition: 'border-color .15s, background-color .15s',
				}}
			>
				<IconButton
					size="small"
					aria-label={active ? '稼働中' : 'このシナリオに切り替える'}
					disabled={active || disabled}
					onClick={requestSwitch}
					sx={{ color: active ? 'primary.main' : 'text.disabled', p: 0.5 }}
				>
					{active ? <RadioButtonCheckedIcon /> : <RadioButtonUncheckedIcon />}
				</IconButton>

				<Box sx={{ flex: 1, minWidth: 0 }}>
					<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
						<Typography variant="subtitle1" fontWeight={600} noWrap>
							{scenario.name || '(名称未設定)'}
						</Typography>
						{active && (
							<Chip
								size="small"
								color="success"
								icon={<CheckCircleIcon />}
								label="稼働中"
								sx={{ height: 20, '& .MuiChip-label': { px: 0.75 } }}
							/>
						)}
					</Box>
					<ScenarioGanttMini scenario={scenario} />
				</Box>

				<Box
					sx={{
						display: 'flex',
						flexDirection: 'column',
						alignItems: 'flex-end',
						gap: 0.5,
						flexShrink: 0,
					}}
				>
					{active ? (
						<Typography variant="caption" color="text.secondary">
							選択中
						</Typography>
					) : (
						<Button
							variant="contained"
							size="small"
							disableElevation
							disabled={disabled}
							onClick={requestSwitch}
						>
							切り替える
						</Button>
					)}
					<Button
						variant="text"
						size="small"
						endIcon={<ChevronRightIcon />}
						onClick={() =>
							history(`/houses/${scenario.houseId}/scenarios/${scenario.id}`)
						}
						sx={{ minWidth: 0 }}
					>
						詳細
					</Button>
				</Box>
			</Box>

			<Dialog
				open={confirmOpen}
				onClose={() => setConfirmOpen(false)}
				maxWidth="xs"
			>
				<DialogTitle
					sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1 }}
				>
					<SwapHorizIcon color="primary" />
					自動制御を切り替えますか？
				</DialogTitle>
				<DialogContent>
					<DialogContentText component="div">
						稼働中の自動制御が
						<Box
							component="span"
							sx={{ fontWeight: 700, color: 'text.primary' }}
						>
							「{scenario.name || '(名称未設定)'}」
						</Box>
						に切り替わります。ハウスの制御内容が変わります。
					</DialogContentText>
				</DialogContent>
				<DialogActions sx={{ px: 3, pb: 2 }}>
					<Button onClick={() => setConfirmOpen(false)} color="inherit">
						キャンセル
					</Button>
					<Button
						onClick={doSwitch}
						variant="contained"
						disableElevation
						disabled={disabled}
						autoFocus
					>
						切り替える
					</Button>
				</DialogActions>
			</Dialog>
		</ListItem>
	)
}

export default ScenarioRow
